import { loadingState, beginRefresh } from '../core/loadingState.js';
import { captureTemplateEntry, readTemplateEntry, saveTemplateEntry, clearTemplateEntry } from '../core/templateEntry.js';
/**
 * templates-route.js — client-saas /templates
 *
 * A browse view for the starter flows. Until now /templates was one of the generic
 * SECTION_ROUTES placeholders, so the side-nav item and the dashboard both led to a
 * stub even though the gallery content already existed — it was only reachable from
 * the "Template" button inside the Magnets page.
 *
 * Deliberately NOT a second provisioning path. Choosing a template here parks the
 * choice via setPendingTemplate() and navigates to #/magnets, which claims it on
 * init and runs the one existing flow (owner picker → provisionDraft → applyTemplate
 * → open the edit modal). Duplicating that here would mean a second place to keep
 * correct, and it depends on magnets-route's loaded state anyway.
 *
 * Preview is the exception, and only because it provisions nothing: it renders the
 * template's own steps through the SAME openPreviewModal()/magnetToPackage() pair the
 * magnet card and flow builder use, so what you see before committing is what the
 * template actually builds.
 *
 * Cards come from templateCardHTML() — the same renderer the picker modal uses, so
 * the two views cannot describe a template differently.
 */

import AuthController from '../services/AuthController.js';
import { templatePackage } from '@sse/shared';
import { openPreviewModal, magnetToPackage } from './magnet-preview.js';
import {
  TEMPLATE_CATEGORIES,
  getTemplates,
  loadTemplates,
  templateCardHTML,
  ensureTemplateStyles,
  setPendingTemplate,
} from './magnet-templates.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

// Starting a magnet is the same right the Magnets page gates its New/Template buttons
// on. A managed client whose reseller builds their magnets for them still gets the full
// browse + preview experience; only the commit action goes away.
const USE_CAPS = ['provision_magnets', 'manage_magnets'];
const canUseTemplates = () => AuthController.getCapabilities().some((c) => USE_CAPS.includes(c));

// This route is PUBLIC (see main.js): a signed-out visitor browses the same
// gallery. Their Use button stays — it is the conversion hook — but it parks
// the choice and routes to signup instead of the (auth-only) magnets page.
const isSignedOut = () => !AuthController.isAuthenticated();

/** [category, count] for every category actually present, in TEMPLATE_CATEGORIES order. */
function categoryCounts() {
  const counts = new Map();
  for (const t of getTemplates()) counts.set(t.category, (counts.get(t.category) || 0) + 1);
  return Object.keys(TEMPLATE_CATEGORIES)
    .filter((c) => counts.has(c))
    .map((c) => [c, counts.get(c)]);
}

const chipHTML = (cat, label, count, pressed) =>
  `<button class="m5t-chip--filter" type="button" data-cat="${esc(cat)}" aria-pressed="${pressed}">${esc(label)}<span class="m5t-chip__count">${count}</span></button>`;

let stylesInjected = false;

function ensureStyles() {
  ensureTemplateStyles(); // .mag-tpl-grid / .mag-tpl-card / gallery face / derived art
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-templates-page-styles';
  style.textContent = `
    .page-shell[data-section="templates"] .tpl-filters { margin: 0 0 20px; }
    .page-shell[data-section="templates"] .settings-note { margin: 0 0 20px; }
    .tpl-selected { max-width: 680px; }
    .tpl-selected > button { margin-bottom: 20px; }
    .tpl-selected .mag-tpl-grid { grid-template-columns: 1fr; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

/** The filter chips + grid, from the current catalog (re-rendered when the live fetch lands). */
function galleryHTML() {
  const entry = readTemplateEntry();
  const selected = getTemplates().find(t => t.id === entry?.slug);
  if (selected) return `<div class="tpl-selected"><button class="btn btn-secondary" type="button" data-browse-templates>← All templates</button><div class="mag-tpl-grid mag-tpl-grid--page" id="tpl-grid">${templateCardHTML(selected, {detail:true, gallery:true, canUse:isSignedOut() || canUseTemplates()})}</div></div>`;
  // Signed out: Use stays visible (it routes to signup). Signed in without
  // provisioning rights (managed client): Use hidden, managed-note shown.
  const canUse = isSignedOut() || canUseTemplates();
  return `
      <div class="m5t-chip-row tpl-filters" id="tpl-filters" role="group" aria-label="Filter templates by category">
        ${chipHTML('all', 'All', getTemplates().length, 'true')}
        ${categoryCounts().map(([cat, count]) => chipHTML(cat, TEMPLATE_CATEGORIES[cat], count, 'false')).join('')}
      </div>

      <div class="mag-tpl-grid mag-tpl-grid--page" id="tpl-grid">
        ${getTemplates().length ? '' : '<p class="settings-note" role="status">No templates are published yet. You can still create a magnet from scratch.</p>'}
        ${getTemplates().map((t) => templateCardHTML(t, { detail: true, gallery: true, canUse })).join('')}
      </div>`;
}

export function render() {
  captureTemplateEntry();
  ensureStyles();
  const signedOut = isSignedOut();
  return `
    <div class="page-shell" data-section="templates">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Templates</h1>
          <p class="page-description">Choose a starting point. Make it your own before publishing.</p>
        </div>
        ${signedOut ? `<a class="btn btn-primary" href="#/signup">
          <span class="material-symbols-outlined" aria-hidden="true">rocket_launch</span>
          Start Free
        </a>` : `<a class="btn btn-secondary" href="#/magnets">
          <span class="material-symbols-outlined" aria-hidden="true">bolt</span>
          Your magnets
        </a>`}
      </div>

      ${(signedOut || canUseTemplates()) ? '' : `<div class="settings-note" id="tpl-readonly-note">
        <span class="material-symbols-outlined" aria-hidden="true">lock</span>
        Setup managed by your provider.
      </div>`}

      <div id="tpl-gallery">${getTemplates().length ? galleryHTML() : loadingState('cards', 'Loading templates')}</div>
    </div>`;
}

function wireGallery() {
  document.querySelector('[data-browse-templates]')?.addEventListener('click', () => {
    clearTemplateEntry(); document.getElementById('tpl-gallery').innerHTML = galleryHTML(); wireGallery();
  });
  const grid = document.getElementById('tpl-grid');

  document.getElementById('tpl-filters')?.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-cat]');
    if (!chip) return;
    // aria-pressed is the state, not a decoration: the chips are a single-choice
    // group, so exactly one is pressed and the cards follow it.
    chip.parentElement.querySelectorAll('[data-cat]').forEach((c) => {
      c.setAttribute('aria-pressed', String(c === chip));
    });
    const cat = chip.dataset.cat;
    grid?.querySelectorAll('.mag-tpl-card').forEach((card) => {
      card.hidden = !(cat === 'all' || card.dataset.cat === cat);
    });
  });

  grid?.addEventListener('click', (e) => {
    const preview = e.target.closest('[data-preview]');
    if (preview) {
      const t = getTemplates().find((x) => x.id === preview.dataset.preview);
      // Preview the same complete template settings used by the new-draft path.
      if (t) openPreviewModal(() => magnetToPackage({ definition: templatePackage(t) }));
      return;
    }
    const btn = e.target.closest('[data-tpl]');
    if (!btn) return;
    setPendingTemplate(btn.dataset.tpl);
    if (isSignedOut()) saveTemplateEntry(btn.dataset.tpl, true);
    else clearTemplateEntry();
    // Hash assignment rather than router.navigate(): matches how the rest of the app
    // moves between sections (the nav and every view-all link are plain #/ hrefs).
    // Signed out: same park, but through signup — #/magnets would just bounce to
    // signin and strand the choice.
    window.location.hash = isSignedOut() ? '#/signup' : '#/magnets';
  });
}

export function init() {
  wireGallery();
  // The first paint used whatever catalog was in hand (usually the bundled
  // fallback). When the live list lands and differs, swap the gallery in place —
  // filters reset, which is fine on a page that just appeared.
  const before = getTemplates();
  const gallery = document.getElementById('tpl-gallery');
  const finishRefresh = beginRefresh(gallery, 'Updating templates');
  loadTemplates().then((after) => {
    const mount = document.getElementById('tpl-gallery');
    finishRefresh();
    if (!mount || mount !== gallery || (after === before && before.length)) return;
    mount.innerHTML = galleryHTML();
    wireGallery();
  });
}

export default { render, init };
